import { gsap } from 'gsap';

export const HOVER_INTENT_MS = 80;
const OPEN_TIME_SCALE = 1.4;
const NORMAL_REVERSE_TIME_SCALE = 1.15;
const SWITCH_PROGRESS = 0.34;
const SWITCH_OUT_DURATION = 0.16;

/**
 * GOLDEN — EXACT port of Codrops ProductPreview (product-preview.js).
 * Moved verbatim from the validated Hover Reference Test. Do not tune.
 */
export default class ProductPreview {
  ui: {
    products: HTMLElement[];
    container: HTMLElement;
    clipped: HTMLElement | null;
    title: HTMLElement | null;
    price: HTMLElement | null;
    allPreviewImages: HTMLCollection;
    previewImagesPerID: Record<string, HTMLElement[]>;
  };

  scaleFactor: { x: number; y: number } = { x: 5, y: 5 };

  armWidth = { x: 10, y: 10 };

  timeline: gsap.core.Timeline | null = null;

  private visualItem: HTMLElement | null = null;

  private pendingItem: HTMLElement | null = null;

  private switchTween: gsap.core.Tween | null = null;

  private switching = false;

  constructor({ products, container }: { products: HTMLElement[]; container: HTMLElement }) {
    const allPreviewImages = container.querySelector('.product-preview__images')!.children;
    const previewImages: Record<string, HTMLElement[]> = {};
    Array.from(allPreviewImages).forEach((el) => {
      const img = el as HTMLElement;
      const id = img.dataset.id as string;
      if (!previewImages[id]) {
        previewImages[id] = [];
      }
      previewImages[id].push(img);
    });

    this.ui = {
      products,
      container,
      clipped: container.querySelector('.masked-preview'),
      title: container.querySelector('.product-title'),
      price: container.querySelector('.product-price'),
      allPreviewImages,
      previewImagesPerID: previewImages,
    };

    this.init();
  }

  init() {
    this.onResize();
  }

  setProduct(product: HTMLElement | null) {
    if (product) {
      const atRest = !this.switching && (this.timeline?.progress() ?? 0) === 0;
      if (!this.visualItem || atRest) this.openItem(product);
      else this.switchTo(product);
    } else if (this.switching) {
      this.pendingItem = null;
    } else {
      this.closePreview();
    }
  }

  /** Instant selection. No tween, no crossfade, no src change. */
  selectItem(item: HTMLElement) {
    gsap.set(this.ui.allPreviewImages, { opacity: 0 });
    const next = this.ui.previewImagesPerID[item.dataset.index as string];
    if (next) gsap.set(next, { opacity: 1 });
    this.updateMeta(item);
  }

  updateMeta(item: HTMLElement) {
    if (this.ui.title) this.ui.title.textContent = item.dataset.name ?? '';
    if (this.ui.price) this.ui.price.textContent = item.dataset.price ?? '';
  }

  openItem(item: HTMLElement) {
    this.visualItem = item;
    this.selectItem(item);
    this.timeline?.timeScale(OPEN_TIME_SCALE).play();
  }

  closePreview() {
    this.timeline?.timeScale(NORMAL_REVERSE_TIME_SCALE).reverse();
  }

  switchTo(item: HTMLElement) {
    if (!this.switching && this.visualItem === item) {
      this.timeline?.timeScale(OPEN_TIME_SCALE).play();
      return;
    }
    this.pendingItem = item;
    if (this.switching) return;
    this.switching = true;
    this.moveToSwitchGate();
  }

  moveToSwitchGate() {
    this.switchTween?.kill();
    const tl = this.timeline;
    if (!tl || tl.progress() <= SWITCH_PROGRESS) {
      this.commitPendingItem();
      return;
    }
    tl.pause();
    this.switchTween = gsap.to(tl, {
      progress: SWITCH_PROGRESS,
      duration: SWITCH_OUT_DURATION,
      ease: 'power2.out',
      overwrite: true,
      onComplete: () => {
        this.switchTween = null;
        this.commitPendingItem();
      },
    });
  }

  commitPendingItem() {
    const next = this.pendingItem;
    this.pendingItem = null;
    if (!next) {
      this.switching = false;
      this.closePreview();
      return;
    }
    this.selectItem(next);
    this.visualItem = next;
    this.switching = false;
    this.timeline?.timeScale(OPEN_TIME_SCALE).play();
  }

  buildTimeline() {
    const { x, y } = this.armWidth;

    this.timeline = gsap
      .timeline({ paused: true, defaults: { ease: 'power2.inOut' }, onUpdate: () => this.syncGridState() })
      .addLabel('preview', 0)
      .addLabel('products', 0)
      .to(this.ui.container, { opacity: 1 }, 'preview')
      .to(this.ui.container, { scaleX: this.scaleFactor.x, scaleY: this.scaleFactor.y, transformOrigin: 'center center' }, 'preview')
      .to(
        this.ui.products,
        {
          opacity: 0,
          x: (i: number) => (i % 2 === 0 ? '2.5vw' : '-2.5vw'),
          y: (i: number) => (i < 2 ? '2.5vw' : '-2.5vw'),
        },
        'products',
      )
      .fromTo(
        this.ui.clipped,
        {
          clipPath: `polygon(
        ${50 - x / 2}% 0%,
        ${50 + x / 2}% 0%,
        ${50 + x / 2}% ${50 - y / 2}%,
        100% ${50 - y / 2}%,
        100% ${50 + y / 2}%,
        ${50 + x / 2}% ${50 + y / 2}%,
        ${50 + x / 2}% 100%,
        ${50 - x / 2}% 100%,
        ${50 - x / 2}% ${50 + y / 2}%,
        0% ${50 + y / 2}%,
        0% ${50 - y / 2}%,
        ${50 - x / 2}% ${50 - y / 2}%
      )`,
        },
        {
          clipPath: `polygon(
        50% 0%,
        50% 0%,
        50% 50%,
        100% 50%,
        100% 50%,
        50% 50%,
        50% 100%,
        50% 100%,
        50% 50%,
        0% 50%,
        0% 50%,
        50% 50%
        )`,
        },
        'preview',
      );
  }

  /** Visual-only mirror of the existing timeline state for the border overlay. No timing/geometry effect. */
  private gridActive = false;

  syncGridState() {
    const active = (this.timeline?.progress() ?? 0) > 0;
    if (active === this.gridActive) return;
    this.gridActive = active;
    const side = this.ui.container.classList.contains('--left') ? 'left' : 'right';
    const cell = this.ui.container.closest<HTMLElement>('[data-grid-cell]');
    if (!cell) return;
    if (active) cell.setAttribute(`data-grid-preview-${side}`, 'active');
    else cell.removeAttribute(`data-grid-preview-${side}`);
  }

  onResize() {
    const { width, height } = this.ui.container.getBoundingClientRect();
    const vw = window.innerWidth / 100;

    const armWidthVw = 5;
    const armWidthPx = armWidthVw * vw;

    this.armWidth = {
      x: (armWidthPx / width) * 100,
      y: (armWidthPx / height) * 100,
    };

    const widthInVw = width / vw;
    const heightInVw = height / vw;
    const shrinkVw = 5;

    this.scaleFactor = {
      x: (widthInVw - shrinkVw) / widthInVw,
      y: (heightInVw - shrinkVw) / heightInVw,
    };

    this.rebuildTimeline();
  }

  rebuildTimeline() {
    this.switchTween?.kill();
    this.switchTween = null;
    this.switching = false;
    this.pendingItem = null;
    this.visualItem = null;
    this.timeline?.kill();
    this.buildTimeline();
    this.syncGridState();
  }

  destroy() {
    this.switchTween?.kill();
    this.timeline?.kill();
    this.timeline = null;
  }
}